import Vue, { VueConstructor } from 'vue';
import VueEvents from 'vue-events';
import VueCookie from 'vue-cookie';
import Vue2TouchEvents from 'vue2-touch-events';
import ShoppingCartPage from '@/cart-page/CartPage.vue';
import ShoppingCartButton from '@/header-button/ShoppingCartButton.vue';
import ProductSearchForm from '@/search-form/ProductSearchForm.vue';
import UserMenu from '@/header/UserMenu.vue';
import DetailPurchaseSection from '@/product-detail/DetailPurchaseSection.vue';
import ImageSection from '@/product-detail/ImageSection.vue';
import EssenceList from '@/product-detail/EssenceList.vue';
import CustomSelectTh from '@/common/CustomSelectTh.vue';
import ProductProperties from '@/product-detail/ProductProperties.vue';
import ProductInformation from '@/product-detail/ProductInformation.vue';
import LanguageSelector from '@/header/LanguageSelector.vue';
import CustomerSelector from '@/header/CustomerSelector.vue';
import OrderTemplateEditForm from '@/order-template/OrderTemplateEditForm.vue';
import OrderTemplateButtons from '@/order-template/OrderTemplateButtons.vue';
import store from '@/store';
import { ConfigService } from '@/services/ConfigService';
import MessageBar from '@/header/MessageBar.vue';
import OrderListsDropdown from '@/header/OrderListsDropdown.vue';
import OrderTemplateDeleteButton from '@/order-template/OrderTemplateDeleteButton.vue';
import OrderTemplatePdfButton from '@/order-template/OrderTemplatePdfButton.vue';
import FeaturedProducts from '@/cms/FeaturedProducts.vue';
import Slider from '@/cms/Slider.vue';
import InventoriesDeleteButton from '@/inventories/InventoriesDeleteButton.vue';
import MessageAfterRefresh from '@/message/MessageAfterRefresh.vue';
import LabelPrintItem from '@/label-print/LabelPrintItem.vue';
import LabelSelection from '@/common/LabelSelection.vue';
import LabelSelectionButton from '@/header-button/LabelSelectionButton.vue';
import LabelPrintList from '@/label-print/LabelPrintList.vue';
import DatePickerTh from '@/common/DatePickerTh.vue';
import PromoProducts from '@/cms/PromoProducts.vue';
import AddAllEssencesToLabelSelectionButton from '@/essential/AddAllEssencesToLabelSelectionButton.vue';
import Gallery from '@/cms/Gallery.vue';
import TeaserTiles from '@/cms/TeaserTiles.vue';
import VueI18n from 'vue-i18n';
import messages from '@/i18n/messages';
import BestFriendOrderItems from '@/bf-order/BestFriendOrderItems.vue';
import WindowWidthResizeListener from '@/common/WindowWidthResizeListener.vue';
import RecommendedProducts from '@/cms/RecommendedProducts.vue';
import NotificationButton from '@/header-button/NotificationButton.vue';
import NotificationPreferences from '@/notifications/NotificationPreferences.vue';
import NotificationCenter from '@/notifications/NotificationCenter.vue';
import DetailPurchaseAndOrderTemplateSection from '@/product-detail/DetailPurchaseAndOrderTemplateSection.vue';
import { DependencyService } from '@/services/DependencyService';
import FairCartPage from '@/fair-cart/FairCartPage.vue';
import EssenceDropdownTh from '@/essential/EssenceDropdownTh.vue';
import ProductListItem from '@/search-result/ProductListItem.vue';
import MatomoSourceService from '@/services/matomo/MatomoSourceService';
import TrackCategoryView from '@/matomo/components/TrackCategoryView.vue';
import TrackProductView from '@/matomo/components/TrackProductView.vue';
import TrackSearchPageVisit from '@/matomo/components/TrackSearchPageVisit.vue';
import MatomoInitializerService from '@/services/matomo/MatomoInitializerService';
import MatomoConfigService from '@/services/matomo/MatomoConfigService';
import CookieModal from '@/cookie-notice/CookieModal.vue';
import CookiePreferencePage from '@/cookie-notice/CookiePreferencePage.vue';
import NotificationModal from '@/notifications/modal/NotificationModal.vue';

Vue.config.productionTip = false;

Vue.use(VueI18n);
Vue.use(VueEvents);
Vue.use(VueCookie);
Vue.use(Vue2TouchEvents);

export const i18n = new VueI18n({ locale: document.documentElement.lang, messages , fallbackLocale: 'de' });

function initConfigService(elementOrSelector: HTMLElement | string): { el: HTMLElement, localSettings: any } {
  let el: HTMLElement;
  if (typeof elementOrSelector === 'string') {
    el = document.querySelector(elementOrSelector);
  } else {
    el = elementOrSelector;
  }
  let localSettings;
  if (el) {
    localSettings = ConfigService.applySettings(el);
  }
  return {
    localSettings,
    el
  };
}

function initDependencyService(elementId: string) {
  const el = document.getElementById(elementId);
  DependencyService.configure(el);
}

function initMatomoSourceService(elementId: string) {
  const el = document.getElementById(elementId);
  MatomoSourceService.singleton().configure(el);
}

function includeTrackingScript() {
  MatomoConfigService.singleton().initializeFromElement(document.getElementById('include-matomo-script'));
  MatomoInitializerService.singleton().initializeMatomo();
}

function makeVue(app: VueConstructor<Vue>, elementOrSelector: HTMLElement | string) {
  const wrapper = initConfigService(elementOrSelector);
  if (!wrapper.el) {
    return;
  }
  new Vue({
    store,
    i18n,
    render(r) {
      return r(app, {
        props: wrapper.localSettings
      });
    }
  }).$mount(wrapper.el);
}

initConfigService('#init-config-service');
initDependencyService('init-dependency-service');
initMatomoSourceService('init-matomo-service');
includeTrackingScript();
// Initializes customer data, tenant data, keycloak token, hub url, image url, language
makeVue(ShoppingCartButton, '#shopping-cart-button-vue');
// Initializes username and keycloak url
makeVue(UserMenu, '#vue-user-menu');
makeVue(LabelSelectionButton, '#label-button-vue');
makeVue(NotificationButton, '#notif-button-vue');
makeVue(CustomerSelector, '#vue-customer-selector');
makeVue(ShoppingCartPage, '#shopping-cart-page-vue');
makeVue(FairCartPage, '#fair-cart-page-vue');
makeVue(ProductSearchForm, '#product-search-input-vue');
makeVue(ProductSearchForm, '#stocklist-product-search-input-vue');
makeVue(DetailPurchaseSection, '#pd-vue-purchase');
makeVue(DetailPurchaseAndOrderTemplateSection, '#pd-vue-purchase-and-ot');
makeVue(ImageSection, '#pd-vue-images');
makeVue(EssenceList, '#pd-essence-list');
makeVue(ProductProperties, '#pd-vue-properties');
makeVue(ProductInformation, '#pd-vue-information');
makeVue(OrderTemplateEditForm, '#ot-detail-edit-vue');
makeVue(MessageBar, '#hd-err-bar-vue');
makeVue(OrderListsDropdown, '#order-lists-dropdown-vue');
makeVue(CookieModal, '#cookie-notice-vue');
makeVue(CookiePreferencePage, '#cookie-preferences-vue');
makeVue(OrderTemplateDeleteButton, '#delete-ordertemplate-button');
makeVue(OrderTemplateButtons, '#add-ordertemplate-to-cart-button');
makeVue(MessageAfterRefresh, '#message-after-refresh');
makeVue(LabelPrintList, '#label-print-list');
makeVue(OrderTemplatePdfButton, '#essence-pdf-btn-vue');
makeVue(AddAllEssencesToLabelSelectionButton, '#add-all-to-labelselection-vue');
makeVue(TeaserTiles, '#cms-tsr-tiles-vue');
makeVue(WindowWidthResizeListener, '#global-window-width-resize-listener');
makeVue(LanguageSelector, '#language-selector-vue');
makeVue(NotificationPreferences, '#user-profile-notification-preferences-vue');
makeVue(NotificationCenter, '#notification-center-content-vue');
makeVue(NotificationModal, '#notification-modal-vue');
makeVue(TrackCategoryView, '#matomo-track-category-vue');
makeVue(TrackProductView, '#matomo-track-product-view-vue');
makeVue(TrackSearchPageVisit, '#matomo-track-product-search-view-vue');

Array.from(document.querySelectorAll<HTMLElement>('.date-picker'))
  .forEach((el) => makeVue(DatePickerTh, el));
Array.from(document.querySelectorAll<HTMLElement>('.pd-vue-label-selection'))
  .forEach((el) => makeVue(LabelSelection, el));
Array.from(document.querySelectorAll<HTMLElement>('.product-list-item-vue'))
  .forEach((el) => makeVue(ProductListItem, el));
Array.from(document.querySelectorAll<HTMLElement>('.essence-dropdown'))
  .forEach((el) => makeVue(EssenceDropdownTh, el));
Array.from(document.querySelectorAll<HTMLElement>('.custom-select'))
  .forEach((el) => makeVue(CustomSelectTh, el));
Array.from(document.querySelectorAll<HTMLElement>('.bestfriend-order-item'))
  .forEach((el) => makeVue(BestFriendOrderItems, el));
Array.from(document.querySelectorAll<HTMLElement>('.cms-fp-vue'))
  .forEach((el) => makeVue(FeaturedProducts, el));
Array.from(document.querySelectorAll<HTMLElement>('.cms-rp-vue'))
    .forEach((el) => makeVue(RecommendedProducts, el));
Array.from(document.querySelectorAll<HTMLElement>('.cms-sl-vue'))
  .forEach((el) => makeVue(Slider, el));
Array.from(document.querySelectorAll<HTMLElement>('.cms-gal-vue'))
  .forEach((el) => makeVue(Gallery, el));
Array.from(document.querySelectorAll<HTMLElement>('.delete-inventory-button'))
  .forEach((el) => makeVue(InventoriesDeleteButton, el));
Array.from(document.querySelectorAll<HTMLElement>('.label-print-item'))
  .forEach((el) => makeVue(LabelPrintItem, el));
Array.from(document.querySelectorAll<HTMLElement>('.promo-products'))
  .forEach((el) => makeVue(PromoProducts, el));
